/**
 * One restaurant: who owns it, its sites, its plan, its invoices, and the
 * platform's own record of what was done to it.
 *
 * Read in the restaurant's own session on the server, so this page shows
 * exactly what the restaurant's rows say. Suspending asks for a reason and
 * says what it does first: the owner sees the reason in their audit log.
 *
 * The Plan card moves the plan directly (up or down, with or without an
 * invoice) and holds the free trial: start one, extend it, end it now, or
 * convert it to paid — which is changing the plan, opened on the trial's.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ApiError,
  displayLocale,
  formatMoney,
  usePlatformPlanChoices,
  usePlatformRestaurant,
  useSetRestaurantSuspended,
  type PlatformInvoice,
  type PlatformRestaurantDetail,
} from '@restaurant/api-client';
import {
  ActionError,
  Badge,
  Button,
  Card,
  Empty,
  ErrorNotice,
  Field,
  Input,
  Loading,
  Sheet,
} from '@restaurant/ui';

import {
  ApproveSheet,
  DeclineSheet,
  InvoiceState,
  IssueInvoiceSheet,
  PaidSheet,
  VoidSheet,
  type RequestFacts,
} from '../billing-sheets';
import {
  ChangePlanSheet,
  EndTrialSheet,
  ExtendTrialSheet,
  StartTrialSheet,
} from '../plan-sheets';
import { SupportCard } from '../support-card';
import { cycleWords, dayOf, daysLeftWords, formatDay, formatMoment, isOverdue } from '../words';

type Open =
  | { name: 'suspend' }
  | { name: 'approve'; request: RequestFacts }
  | { name: 'decline'; request: RequestFacts }
  | { name: 'issue' }
  | { name: 'paid'; invoice: PlatformInvoice }
  | { name: 'void'; invoice: PlatformInvoice }
  | { name: 'change'; startWith?: string }
  | { name: 'trial' }
  | { name: 'extend' }
  | { name: 'end-trial' }
  | null;

export function RestaurantPage() {
  const { restaurantId = '' } = useParams();
  const detail = usePlatformRestaurant(restaurantId);
  const choices = usePlatformPlanChoices(restaurantId);
  const [open, setOpen] = useState<Open>(null);
  const locale = displayLocale();

  if (detail.isPending) return <Loading label="Loading the restaurant" />;
  if (detail.isError) {
    return detail.error instanceof ApiError && detail.error.status === 404 ? (
      <Empty title="No such restaurant" description="It may have been removed." />
    ) : (
      <ErrorNotice message={detail.error.message} />
    );
  }
  const r = detail.data;
  const sub = r.subscription;
  const pending = sub.pending;
  const trial = sub.trial;
  const request: RequestFacts | null = pending
    ? {
        id: pending.id,
        restaurantId: r.id,
        restaurantName: r.name,
        planName: pending.plan.name,
        cycle: pending.cycle,
        note: pending.note,
        listPrice:
          pending.cycle === 'year' ? pending.plan.price_year : pending.plan.price_month,
        currency: sub.currency,
      }
    : null;
  const close = () => setOpen(null);

  return (
    <>
      <p className="pf__crumbs">
        <Link to="/restaurants">Restaurants</Link> / {r.name}
      </p>
      <header className="pf__head">
        <div>
          <h1 className="pf__title">
            {r.name}{' '}
            {r.status === 'suspended' ? (
              <Badge tone="danger">Suspended</Badge>
            ) : r.status === 'trial' ? (
              <Badge tone="accent">Trial</Badge>
            ) : (
              <Badge tone="ok">Active</Badge>
            )}
          </h1>
          <p className="pf__sub">
            Address <span className="pf__mono">{r.slug}</span> · {r.country} · money in{' '}
            {r.currency}
          </p>
        </div>
        {/* Outlined, not filled: the most drastic thing on the page should be
            easy to find and hard to hit by accident, not the loudest thing. */}
        <Button
          variant={r.status === 'suspended' ? 'primary' : 'secondary'}
          className={r.status === 'suspended' ? '' : 'pf__danger-btn'}
          onClick={() => setOpen({ name: 'suspend' })}
        >
          {r.status === 'suspended' ? 'Lift the suspension' : 'Suspend'}
        </Button>
      </header>

      <div className="pf__split">
        <div className="pf__split-main">
          <Card title="Sites">
            <Sites restaurant={r} />
          </Card>

          <Card
            title="Invoices"
            actions={
              <Button onClick={() => setOpen({ name: 'issue' })}>Issue an invoice</Button>
            }
          >
            {r.invoices.length === 0 ? (
              <p className="pf__sub">No invoices yet.</p>
            ) : (
              <div className="pf__table-wrap">
                <table className="pf__table">
                  <thead>
                    <tr>
                      <th>Number</th>
                      <th>For</th>
                      <th className="pf__num">Amount</th>
                      <th>Due</th>
                      <th>State</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {r.invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="pf__mono">{invoice.number}</td>
                        <td>{invoice.description}</td>
                        <td className="pf__num">
                          {formatMoney(invoice.amount, invoice.currency, locale)}
                        </td>
                        <td>{formatDay(invoice.due_on, locale)}</td>
                        <td>
                          <InvoiceState
                            invoice={invoice}
                            overdue={isOverdue(invoice.status, invoice.due_on)}
                          />
                          {invoice.payment_reference && (
                            <span className="pf__faint"> {invoice.payment_reference}</span>
                          )}
                        </td>
                        <td className="pf__actions">
                          {invoice.status === 'open' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => setOpen({ name: 'paid', invoice })}
                              >
                                Mark paid
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setOpen({ name: 'void', invoice })}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card
            title="What the platform did here"
            actions={<Link to={`/activity?restaurant=${r.id}`}>All of it</Link>}
          >
            {r.activity.length === 0 ? (
              <p className="pf__sub">Nothing yet.</p>
            ) : (
              <ul className="pf__list">
                {r.activity.map((entry) => (
                  <li key={entry.id}>
                    <span className="pf__faint">{formatMoment(entry.occurred_at, locale)}</span>{' '}
                    {entry.summary} <span className="pf__faint">— {entry.operator_email}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
        <div className="pf__split-side">
          <Card
            title="Plan"
            actions={
              <Button disabled={!choices.data} onClick={() => setOpen({ name: 'change' })}>
                Change plan
              </Button>
            }
          >
            <dl className="pf__facts">
              <dt>Plan</dt>
              <dd>
                {sub.plan.name}
                {trial ? ', free' : `, billed ${cycleWords(sub.cycle)}`}
              </dd>
              <dt>Status</dt>
              <dd>
                {r.status === 'suspended'
                  ? 'Suspended: paid-for features stopped'
                  : r.status === 'trial'
                    ? 'On a free trial'
                    : 'Active'}
              </dd>
              <dt>Paid up to</dt>
              <dd>
                {sub.current_period_end
                  ? formatMoment(sub.current_period_end, locale)
                  : 'Not set'}
              </dd>
            </dl>
            {pending && request && (
              <div className="pf__pending">
                <p className="pf__sub">
                  Asked {formatMoment(pending.requested_at, locale)} to move to{' '}
                  <strong>{pending.plan.name}</strong>, billed {cycleWords(pending.cycle)}.
                  {pending.note ? ` “${pending.note}”` : ''}
                </p>
                <div className="pf__row">
                  <Button
                    variant="primary"
                    onClick={() => setOpen({ name: 'approve', request })}
                  >
                    Approve
                  </Button>
                  <Button onClick={() => setOpen({ name: 'decline', request })}>Decline</Button>
                </div>
              </div>
            )}
            <div className="pf__trial">
              <p className="pf__label">Free trial</p>
              {trial ? (
                <>
                  <p className="pf__sub">
                    <strong>
                      {trial.plan.name} until {formatDay(dayOf(trial.ends_at), locale)}
                    </strong>{' '}
                    ({daysLeftWords(trial.days_left)}) — then back to {trial.previous_plan.name}
                    , billed {cycleWords(trial.previous_cycle)}.
                    {trial.started_by_email ? ` Given by ${trial.started_by_email}.` : ''}
                    {trial.note ? ` “${trial.note}”` : ''}
                  </p>
                  <div className="pf__row">
                    <Button
                      variant="primary"
                      disabled={!choices.data}
                      onClick={() => setOpen({ name: 'change', startWith: trial.plan.key })}
                    >
                      Convert to paid
                    </Button>
                    <Button
                      disabled={!choices.data}
                      onClick={() => setOpen({ name: 'extend' })}
                    >
                      Extend
                    </Button>
                    <Button variant="ghost" onClick={() => setOpen({ name: 'end-trial' })}>
                      End now
                    </Button>
                  </div>
                </>
              ) : r.status === 'suspended' ? (
                <p className="pf__sub">None. Lift the suspension before giving one.</p>
              ) : (
                <div className="pf__row">
                  <p className="pf__sub">None running.</p>
                  <Button disabled={!choices.data} onClick={() => setOpen({ name: 'trial' })}>
                    Start a free trial
                  </Button>
                </div>
              )}
            </div>
          </Card>
          <Card title="Owners">
            {r.owners.length === 0 ? (
              <p className="pf__sub">Nobody holds every permission here.</p>
            ) : (
              <ul className="pf__list">
                {r.owners.map((o) => (
                  <li key={o.id} className="pf__person">
                    <strong>{o.name}</strong>
                    <a href={`mailto:${o.email}`}>{o.email}</a>
                    {!o.is_active && <span className="pf__faint">Login switched off</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <SupportCard restaurantId={r.id} />
        </div>
      </div>

      {open?.name === 'suspend' && <SuspendSheet restaurant={r} onDismiss={close} />}
      {open?.name === 'approve' && <ApproveSheet request={open.request} onDismiss={close} />}
      {open?.name === 'decline' && <DeclineSheet request={open.request} onDismiss={close} />}
      {open?.name === 'issue' && (
        <IssueInvoiceSheet
          restaurants={[]}
          fixedRestaurant={{ id: r.id, name: r.name }}
          currency={sub.currency}
          onDismiss={close}
        />
      )}
      {open?.name === 'paid' && (
        <PaidSheet restaurantId={r.id} invoice={open.invoice} onDismiss={close} />
      )}
      {open?.name === 'void' && (
        <VoidSheet restaurantId={r.id} invoice={open.invoice} onDismiss={close} />
      )}
      {open?.name === 'change' && choices.data && (
        <ChangePlanSheet
          restaurant={r}
          choices={choices.data}
          cycle={trial ? trial.previous_cycle : sub.cycle}
          trial={trial}
          startWith={open.startWith}
          onDismiss={close}
        />
      )}
      {open?.name === 'trial' && choices.data && (
        <StartTrialSheet
          restaurant={r}
          choices={choices.data}
          cycle={sub.cycle}
          onDismiss={close}
        />
      )}
      {open?.name === 'extend' && trial && choices.data && (
        <ExtendTrialSheet
          restaurant={r}
          trial={trial}
          maxDays={choices.data.trial_max_days}
          onDismiss={close}
        />
      )}
      {open?.name === 'end-trial' && trial && (
        <EndTrialSheet restaurant={r} trial={trial} choices={choices.data} onDismiss={close} />
      )}
    </>
  );
}

function Sites({ restaurant }: { restaurant: PlatformRestaurantDetail }) {
  if (restaurant.sites.length === 0) return <p className="pf__sub">No sites.</p>;
  return (
    <div className="pf__table-wrap">
      <table className="pf__table">
        <thead>
          <tr>
            <th>Site</th>
            <th>Code</th>
            <th>Time zone</th>
            <th>Currency</th>
            <th>Open</th>
          </tr>
        </thead>
        <tbody>
          {restaurant.sites.map((s) => (
            <tr key={s.id}>
              <td>{s.name}</td>
              <td className="pf__mono">{s.code}</td>
              <td>{s.timezone}</td>
              <td>{s.currency}</td>
              <td>{s.is_active ? 'Yes' : 'Closed'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SuspendSheet({
  restaurant,
  onDismiss,
}: {
  restaurant: PlatformRestaurantDetail;
  onDismiss: () => void;
}) {
  const change = useSetRestaurantSuspended();
  const [reason, setReason] = useState('');
  const suspending = restaurant.status !== 'suspended';
  return (
    <Sheet
      title={
        suspending ? `Suspend ${restaurant.name}?` : `Lift ${restaurant.name}'s suspension?`
      }
      tone={suspending ? 'danger' : 'neutral'}
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant={suspending ? 'danger' : 'primary'}
            size="lg"
            block
            disabled={reason.trim() === ''}
            loading={change.isPending}
            onClick={() =>
              change.mutate(
                { id: restaurant.id, suspended: suspending, reason: reason.trim() },
                { onSuccess: onDismiss },
              )
            }
          >
            {suspending ? 'Suspend it' : 'Lift the suspension'}
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        {suspending
          ? 'Everything stays readable and nothing is deleted, but paid-for features stop taking changes until the suspension is lifted. The owner sees your reason in their audit log.'
          : 'Paid-for features take changes again at once. The owner sees your reason in their audit log.'}
      </p>
      <Field label="Reason">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
      <ActionError
        error={change.error}
        action={suspending ? 'Suspending' : 'Lifting the suspension'}
      />
    </Sheet>
  );
}
